import React, { MouseEvent, useState, useEffect, useRef } from 'react';
import { SidebarMenuGroup } from '../sidebar-menu-group';
import { getActiveRuleSetFromUrl } from '../../../utils/UrlUtil';
import { SidebarRoleSwitcher } from '../../SidebarRoleSwitcher';
import { SidebarSwitcher } from '../../SidebarSwitcher';
import { WindowHelperWrapper } from '../../../utils/WindowHelper';
import { cloneDeep } from 'lodash';
import { MenuData, MenuItem, SidebarMenuProps } from './types';
import { useOutsideClick } from '@chakra-ui/react';
import { onExecuteKeyDown } from '../../../utils/KeyboardUtil';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { ChakraUIProvider } from '../../../react-on-rails/ChakraUIProvider';

const EXPANDED_SIDEBAR_BREAKPOINT = 1080;

const SCORM_MANAGER_BLACKLIST = Object.freeze([
  /cportal\/teams/,
  /cportal\/user_uploads/,
  /cportal\/rosters/,
  /cportal\/categories/,
  /cportal\/assignments/,
  /cportal\/templates/,
  /cportal\/training_periods/,
  /cportal\/identity_providers/,
  /cportal\/data_integration/,
  /cportal\/webhooks/,
]);

export const SidebarMenu: React.FC<SidebarMenuProps> = ({
  currentRuleSets,
  currentUrl,
  frontEndDomain,
  menuData,
  navigate,
  windowOverride,
  toggleButtonTitle,
  buttonAriaLabel,
  options,
  optionsFilterLabel,
  onChange,
  theme,
}) => {
  const serverSide = typeof window === 'undefined';
  const windowHelper = WindowHelperWrapper(windowOverride);

  const isCurrentURL = (currentUrl: string) => {
    return (sub_item: MenuItem) => sub_item.target_url && sub_item.target_url.split('#')[0] === currentUrl;
  };

  const calculateShowChildren = (menuData: MenuData, currentUrl: string) => {
    const items = (menuData.submenu_items || []).map(item => {
      const showChildren = item.submenu_items && item.submenu_items.some(isCurrentURL(currentUrl));
      return { ...item, showChildren };
    });
    return { ...menuData, submenu_items: items };
  };

  const [state, setState] = useState({
    serverSide,
    menuData: calculateShowChildren(menuData, currentUrl),
    currentUrl,
    sidebarExpanded: false,
  });

  const nodeRef = useRef<HTMLElement | null>(null);

  const getForcedExpandedState = () =>
    typeof window !== 'undefined' ? window.innerHeight > EXPANDED_SIDEBAR_BREAKPOINT : false;

  useOutsideClick({
    ref: nodeRef,
    enabled: state.sidebarExpanded,
    handler: () => {
      setState(prev => ({ ...prev, sidebarExpanded: false }));
    },
  });

  useEffect(() => {
    if (!state.serverSide) {
      const activeRuleSet = getActiveRuleSetFromUrl(currentRuleSets);
      if (activeRuleSet && isScormManagerAndOnBlacklist(activeRuleSet.name)) {
        redirectToInterstitial(activeRuleSet.name);
      }
    }
  }, [state.serverSide]);

  useEffect(() => {
    if (menuData) {
      setState(prevState => ({
        ...prevState,
        menuData: calculateShowChildren(menuData, currentUrl),
      }));
    }
  }, [menuData]);

  useEffect(() => {
    if (currentUrl) {
      setState(prevState => ({ ...prevState, currentUrl }));
    }
  }, [currentUrl]);

  const pathIsWhitelisted = () => {
    const path = window.location.pathname;
    return (menuData.permitted_whitelist || []).some(permittedPath => path.includes(permittedPath));
  };

  const pathOnScormManagerBlackList = () => {
    const url = window.location.pathname + window.location.search + window.location.hash;
    return SCORM_MANAGER_BLACKLIST.some(regexp => regexp.test(url));
  };

  const isScormManagerAndOnBlacklist = (ruleSet: string) => {
    return ruleSet === 'scorm_manager' && pathOnScormManagerBlackList();
  };

  const redirectToInterstitial = (ruleSet: string) => {
    const redirect = currentRuleSets.find(currentRuleSet => currentRuleSet.name === ruleSet)?.interstitial_redirect;
    if (!redirect) return;

    windowHelper.redirect(sanitizeUrl(redirect));
  };

  const replaceCurrenRuleSetName = (ruleSet: string) => {
    const newUrl = new URL(currentUrl);
    newUrl.searchParams.set('current_rule_set_name', ruleSet);
    windowHelper.redirect(encodeURI(sanitizeUrl(newUrl.toString())));
  };

  const onSelectRuleSet = (ruleSet: string) => {
    if (pathIsWhitelisted() || isScormManagerAndOnBlacklist(ruleSet)) {
      redirectToInterstitial(ruleSet);
    } else {
      replaceCurrenRuleSetName(ruleSet);
    }
  };

  const toggleSidebar = () => {
    if (window.innerWidth < EXPANDED_SIDEBAR_BREAKPOINT) {
      setState(prevState => ({
        ...prevState,
        menuData: {
          ...prevState.menuData,
          submenu_items: prevState.menuData.submenu_items.map(item => ({ ...item, showChildren: false })),
        },
        sidebarExpanded: !prevState.sidebarExpanded,
      }));
    }
  };

  const onGroupSelect = (item: MenuItem, e: MouseEvent<HTMLAnchorElement>) => {
    if (item.submenu_items && item.submenu_items.length > 0) {
      item.showChildren = !item.showChildren;
      setState(prev => ({ ...prev, sidebarExpanded: true }));
    } else {
      if (item.target_url) {
        if (item.new_window) {
          windowHelper.open(item.target_url, '_blank');
        } else {
          setCurrentUrl(item.target_url);
          if (navigate && item.target_url.includes(frontEndDomain)) {
            e.stopPropagation();
            e.preventDefault();
            navigate(item.target_url.replace(frontEndDomain, ''));
          } else {
            windowHelper.redirect(item.target_url);
          }
        }
      }
    }
  };

  const onRoleSwitcherOpen = () => {
    if (!state.sidebarExpanded) {
      toggleSidebar();
    }
  };

  const setCurrentUrl = (url: string) => {
    const updatedMenuData = cloneDeep(state.menuData);
    updatedMenuData.submenu_items.forEach(item => {
      const itemContainsUrl = item.submenu_items && item.submenu_items.some(sub => sub.target_url === url);
      if (!itemContainsUrl) {
        item.showChildren = false;
      }
    });
    setState(prevState => ({ ...prevState, currentUrl: url, menuData: updatedMenuData, sidebarExpanded: false }));
  };

  return (
    <ChakraUIProvider theme={theme}>
      <nav
        ref={nodeRef}
        id="sidebar"
        className={'nav sidebar' + (state.sidebarExpanded ? ' show-labels' : '')}
        role="navigation"
      >
        <SidebarRoleSwitcher
          // @ts-ignore TODO: Migrate to TS
          currentRuleSets={currentRuleSets}
          onMenuOpen={onRoleSwitcherOpen}
          onSelectRuleSet={onSelectRuleSet}
        />
        <SidebarSwitcher label={optionsFilterLabel} options={options} onChange={onChange} />
        <div className="sidebar-menu" id="side-menu">
          {state.menuData.submenu_items.map(item => (
            <SidebarMenuGroup
              currentUrl={state.currentUrl}
              frontEndDomain={frontEndDomain}
              navigate={navigate}
              item={item}
              key={item.title}
              onSelect={onGroupSelect}
              setCurrentUrl={setCurrentUrl}
              sidebarExpanded={getForcedExpandedState() || state.sidebarExpanded}
            />
          ))}
        </div>
        <div
          role="button"
          className="toggle-control"
          onClick={toggleSidebar}
          onKeyDown={onExecuteKeyDown(toggleSidebar)}
          data-action="toggle-control"
          title={toggleButtonTitle}
          aria-label={buttonAriaLabel}
          tabIndex={0}
        />
      </nav>
    </ChakraUIProvider>
  );
};

SidebarMenu.displayName = 'SidebarMenu';
